import { useQuery } from '@apollo/client';

import type {
  ArticleEstimatedReadTimeQuery as ArticleEstimatedReadTimeQueryType,
  ArticleEstimatedReadTimeQueryVariables,
} from 'bundles/unified-seo-content-common/queries/__generated__/ArticleEstimatedReadTimeQuery';
import { ArticleEstimatedReadTimeQuery } from 'bundles/unified-seo-content-common/queries/assemblerQueries';
import { getDeferToCSRQueryOptions } from 'bundles/unified-seo-content-common/utils';

type EstimatedReadTimeProps = {
  estimatedReadTime?: number;
  estimatedReadTimeLabel?: string;
  loading: boolean;
};

/**
 * Custom hook to fetch and return estimated read time data for an article (currently not supporting other SEO content page types).
 *
 * This hook queries the estimated read time and read time label for a given article slug.
 * It returns the data along with the loading state and any potential error information.
 *
 * @param {ArticleEstimatedReadTimeQueryVariables} params - Parameters for the query.
 * @param {string} params.slug - The slug of the article.
 * @returns {EstimatedReadTimeProps} The estimated read time data, label, and loading state.
 */
const useEstimatedReadTimeData = ({ slug }: ArticleEstimatedReadTimeQueryVariables): EstimatedReadTimeProps => {
  const { data, loading, error } = useQuery<ArticleEstimatedReadTimeQueryType, ArticleEstimatedReadTimeQueryVariables>(
    ArticleEstimatedReadTimeQuery,
    {
      skip: !slug,
      variables: { slug },
      ...getDeferToCSRQueryOptions(),
    }
  );

  if (error) {
    return {
      estimatedReadTime: undefined,
      estimatedReadTimeLabel: undefined,
      loading,
    };
  }

  const content = data?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements[0]?.content;

  return {
    estimatedReadTime: content?.estimatedreadingtime,
    estimatedReadTimeLabel: content?.estimatedreadingtimelabel,
    loading,
  };
};

export default useEstimatedReadTimeData;
